import { useEffect, useRef, useState } from "react";
import {
  UserAlreadySignedInError,
  type CustomAuthAccountData,
  type ICustomAuthPublicClientApplication,
  type ResetPasswordCodeRequiredState,
  type ResetPasswordPasswordRequiredState,
  type SignInCodeRequiredState,
  type SignInContinuationState,
  type SignInPasswordRequiredState,
  type SignUpAttributesRequiredState,
  type SignUpCodeRequiredState,
  type SignUpPasswordRequiredState,
  type UserAccountAttributes,
} from "@azure/msal-browser/custom-auth";
import { NativeAuthFlowName, UiError, toUiError } from "./nativeAuthErrors";

export type NativeAuthView = NativeAuthFlowName;

/**
 * Each step holds the SDK state object that carries the continuation token for the next call. SDK
 * states are class instances, so they live in React state only, never in Redux or storage.
 */
export type NativeAuthStep =
  | { kind: "signIn" }
  | { kind: "signInPassword"; username: string; state: SignInPasswordRequiredState }
  | { kind: "signInCode"; username: string; state: SignInCodeRequiredState; codeLength: number }
  | { kind: "signUp" }
  | { kind: "signUpCode"; username: string; state: SignUpCodeRequiredState; codeLength: number }
  | { kind: "signUpPassword"; username: string; state: SignUpPasswordRequiredState }
  | { kind: "resetPassword" }
  | { kind: "resetCode"; username: string; state: ResetPasswordCodeRequiredState; codeLength: number }
  | { kind: "resetNewPassword"; username: string; state: ResetPasswordPasswordRequiredState }
  | { kind: "completing" };

export interface UseNativeAuthOptions {
  client: ICustomAuthPublicClientApplication;
  scopes: string[];
  initialView: NativeAuthView;
  /** Attributes written at sign-up that the user does not type, e.g. the affiliate slug. */
  signUpAttributes?: SignUpAttributes;
  /** The attribute recording consent to the terms, or "" when the tenant has none. */
  termsAttributeName?: string;
  supportEmail?: string | null;
  /** Called with the signed-in account; responsible for acquiring the token and entering the app. */
  onCompleted: (account: CustomAuthAccountData) => Promise<void>;
  /** The SDK cannot continue natively (MFA, extra attributes, unsupported challenge): use the hosted page. */
  onRedirectRequired: (view: NativeAuthView, loginHint?: string) => void;
}

/** Result types whose outcome is "signed in", "needs the hosted page" or "failed". */
interface SignInOutcome {
  data?: CustomAuthAccountData;
  error?: unknown;
  isCompleted(): boolean;
  isFailed(): boolean;
  isMfaRequired(): boolean;
  isAuthMethodRegistrationRequired(): boolean;
}

/**
 * Values are typed as strings by the SDK but are JSON-encoded before they are sent, so a boolean
 * reaches Entra as a JSON boolean — which is what a Boolean attribute such as the terms flag
 * requires. Sending "true" as text fails the tenant's attribute validation.
 */
export type SignUpAttributes = Record<string, string | boolean>;

/** What the sign-up form collects beyond email and password. */
export interface SignUpProfile {
  displayName: string;
  agreedToTerms: boolean;
}

const DISPLAY_NAME_ATTRIBUTE = "displayName";
const DEFAULT_RESEND_SECONDS = 30;

function initialStep(view: NativeAuthView): NativeAuthStep {
  return { kind: view };
}

export function viewOf(step: NativeAuthStep): NativeAuthView {
  if (step.kind.startsWith("signUp")) return "signUp";
  if (step.kind.startsWith("reset")) return "resetPassword";
  return "signIn";
}

export function useNativeAuth(options: UseNativeAuthOptions) {
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const [step, setStep] = useState<NativeAuthStep>(() => initialStep(options.initialView));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [resendAvailableAt, setResendAvailableAt] = useState(0);

  // The attributes sent with the current sign-up, so a later attributes-required step can answer
  // with them instead of handing the user to the hosted page.
  const sentAttributes = useRef<SignUpAttributes>({});

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const startResendCooldown = (seconds: number) => {
    setResendAvailableAt(Date.now() + Math.max(seconds || DEFAULT_RESEND_SECONDS, 1) * 1000);
  };

  const run = async (flow: NativeAuthView, action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      await action();
    } catch (thrown) {
      // Unexpected exception (the SDK normally reports failures on the result). Log without user input.
      console.error("Native authentication request failed", thrown);
      if (mounted.current) {
        setError(toUiError(thrown, flow, { supportEmail: optionsRef.current.supportEmail }));
      }
    } finally {
      if (mounted.current) {
        setBusy(false);
      }
    }
  };

  const fail = (flow: NativeAuthView, sdkError: unknown, loginHint?: string) => {
    const uiError = toUiError(sdkError, flow, { supportEmail: optionsRef.current.supportEmail });

    // Error data only (codes and correlation id) — never the user's input.
    console.error(`[native auth] ${flow} failed`, {
      shown: uiError.message,
      action: uiError.action,
      errorData: (sdkError as { errorData?: unknown } | null)?.errorData,
    });

    switch (uiError.action) {
      case "redirect":
        optionsRef.current.onRedirectRequired(flow, loginHint);
        return;
      case "goToReset":
        setStep({ kind: "resetPassword" });
        setInfo(uiError.message);
        return;
      case "goToSignIn":
        setStep({ kind: "signIn" });
        break;
      case "restart":
        setStep(initialStep(flow));
        break;
    }

    setError(uiError);
  };

  /**
   * The user flow wants attributes alongside email and password. Everything the form collects was
   * already sent, so this answers with those values; only an attribute nobody has an answer for
   * (the user flow collects something this page does not) sends the user to the hosted page,
   * which can collect them all.
   */
  const handleAttributesRequired = async (
    state: SignUpAttributesRequiredState,
    username: string
  ) => {
    let required: string[] = [];
    try {
      required = state.getRequiredAttributes().map((attribute) => attribute.name);
    } catch {
      // Treated as "unknown", which falls through to the hosted page below.
    }

    const held = sentAttributes.current;
    const missing = required.filter((name) => held[name] === undefined);

    if (required.length > 0 && missing.length === 0) {
      console.info("[native auth] answering the sign-up attributes step", { attributes: required });
      const result = await state.submitAttributes(held as UserAccountAttributes);

      if (result.isCompleted()) {
        await signInWithContinuation(result.state, username, "signUp");
      } else if (result.isFailed()) {
        fail("signUp", result.error, username);
      }
      return;
    }

    console.warn(
      "[native auth] sign-up needs attributes this page does not collect; continuing on the hosted page",
      { required, missing }
    );
    optionsRef.current.onRedirectRequired("signUp", username);
  };

  const complete = async (account: CustomAuthAccountData | undefined) => {
    if (!account) {
      console.error("[native auth] completed without account data");
      setError({ message: "Something went wrong. Please try again." });
      return;
    }
    setStep({ kind: "completing" });
    await optionsRef.current.onCompleted(account);
  };

  /** Second factors and authentication-method registration are browser flows the SDK cannot host. */
  const redirectForChallenge = (result: SignInOutcome, username: string) => {
    console.warn("[native auth] sign-in needs a browser step; continuing on the hosted page", {
      mfaRequired: result.isMfaRequired(),
      authMethodRegistrationRequired: result.isAuthMethodRegistrationRequired(),
    });
    optionsRef.current.onRedirectRequired("signIn", username);
  };

  const continueSignIn = async (result: SignInOutcome, username: string) => {
    if (result.isCompleted()) {
      await complete(result.data);
    } else if (result.isMfaRequired() || result.isAuthMethodRegistrationRequired()) {
      redirectForChallenge(result, username);
    } else if (result.isFailed()) {
      fail("signIn", result.error, username);
    }
  };

  /** After sign-up or password reset the SDK can sign in with a continuation token, no password re-entry. */
  const signInWithContinuation = async (
    state: SignInContinuationState,
    username: string,
    flow: "signUp" | "resetPassword"
  ) => {
    // Typed structurally: the result guards narrow `this`, which would leave `never` in the else branch.
    const result: SignInOutcome = await state.signIn({ scopes: optionsRef.current.scopes });

    if (result.isCompleted()) {
      await complete(result.data);
    } else if (result.isMfaRequired() || result.isAuthMethodRegistrationRequired()) {
      redirectForChallenge(result, username);
    } else {
      console.warn("[native auth] sign-in after %s did not complete; asking the user to sign in", flow);
      setStep({ kind: "signIn" });
      setInfo(
        flow === "signUp"
          ? "Your account is ready. Sign in to continue."
          : "Your password has been updated. Sign in with your new password."
      );
    }
  };

  const startSignIn = (username: string, password: string) =>
    run("signIn", async () => {
      const { client, scopes } = optionsRef.current;
      const result = await client.signIn({ username, password, scopes });

      if (result.isPasswordRequired()) {
        await continueSignIn(await result.state.submitPassword(password), username);
        return;
      }

      if (result.isCodeRequired()) {
        setStep({ kind: "signInCode", username, state: result.state, codeLength: result.state.getCodeLength() });
        startResendCooldown(DEFAULT_RESEND_SECONDS);
        setInfo(`We sent a sign-in code to ${username}.`);
        return;
      }

      if (result.isFailed() && result.error?.errorData instanceof UserAlreadySignedInError) {
        // A cached account exists: reuse it rather than asking the user to sign out first.
        const current = client.getCurrentAccount();
        if (current.isCompleted()) {
          await complete(current.data);
          return;
        }
      }

      await continueSignIn(result, username);
    });

  const startSignUp = (username: string, password: string, profile?: SignUpProfile) =>
    run("signUp", async () => {
      const { client, signUpAttributes, termsAttributeName } = optionsRef.current;

      const attributes: SignUpAttributes = { ...signUpAttributes };
      if (profile?.displayName) {
        attributes[DISPLAY_NAME_ATTRIBUTE] = profile.displayName;
      }
      if (termsAttributeName && profile?.agreedToTerms) {
        attributes[termsAttributeName] = true;
      }
      sentAttributes.current = attributes;

      const result = await client.signUp({
        username,
        password,
        attributes: attributes as Record<string, string>,
      });

      if (result.isCodeRequired()) {
        setStep({ kind: "signUpCode", username, state: result.state, codeLength: result.state.getCodeLength() });
        startResendCooldown(result.state.getCodeResendInterval());
        setInfo(`We sent a verification code to ${username}.`);
      } else if (result.isPasswordRequired()) {
        setStep({ kind: "signUpPassword", username, state: result.state });
      } else if (result.isAttributesRequired()) {
        await handleAttributesRequired(result.state, username);
      } else if (result.isFailed()) {
        fail("signUp", result.error, username);
      }
    });

  const startResetPassword = (username: string) =>
    run("resetPassword", async () => {
      const result = await optionsRef.current.client.resetPassword({ username });

      if (result.isCodeRequired()) {
        setStep({ kind: "resetCode", username, state: result.state, codeLength: result.state.getCodeLength() });
        startResendCooldown(DEFAULT_RESEND_SECONDS);
        setInfo(`We sent a verification code to ${username}.`);
      } else if (result.isFailed()) {
        fail("resetPassword", result.error, username);
      }
    });

  const submitCode = (code: string) =>
    run(viewOf(step), async () => {
      switch (step.kind) {
        case "signInCode":
          await continueSignIn(await step.state.submitCode(code), step.username);
          return;

        case "signUpCode": {
          const result = await step.state.submitCode(code);
          if (result.isCompleted()) {
            await signInWithContinuation(result.state, step.username, "signUp");
          } else if (result.isPasswordRequired()) {
            setStep({ kind: "signUpPassword", username: step.username, state: result.state });
          } else if (result.isAttributesRequired()) {
            await handleAttributesRequired(result.state, step.username);
          } else if (result.isFailed()) {
            fail("signUp", result.error, step.username);
          }
          return;
        }

        case "resetCode": {
          const result = await step.state.submitCode(code);
          if (result.isPasswordRequired()) {
            setStep({ kind: "resetNewPassword", username: step.username, state: result.state });
          } else if (result.isFailed()) {
            fail("resetPassword", result.error, step.username);
          }
          return;
        }
      }
    });

  const submitPassword = (password: string) =>
    run(viewOf(step), async () => {
      switch (step.kind) {
        case "signInPassword":
          await continueSignIn(await step.state.submitPassword(password), step.username);
          return;

        case "signUpPassword": {
          const result = await step.state.submitPassword(password);
          if (result.isCompleted()) {
            await signInWithContinuation(result.state, step.username, "signUp");
          } else if (result.isAttributesRequired()) {
            await handleAttributesRequired(result.state, step.username);
          } else if (result.isFailed()) {
            fail("signUp", result.error, step.username);
          }
          return;
        }

        case "resetNewPassword": {
          const result = await step.state.submitNewPassword(password);
          if (result.isCompleted()) {
            await signInWithContinuation(result.state, step.username, "resetPassword");
          } else if (result.isFailed()) {
            fail("resetPassword", result.error, step.username);
          }
          return;
        }
      }
    });

  const resendCode = () =>
    run(viewOf(step), async () => {
      if (Date.now() < resendAvailableAt) {
        return;
      }

      switch (step.kind) {
        case "signInCode": {
          const result = await step.state.resendCode();
          if (result.isCodeRequired()) {
            setStep({ ...step, state: result.state, codeLength: result.state.getCodeLength() });
            startResendCooldown(DEFAULT_RESEND_SECONDS);
            setInfo(`We sent a new code to ${step.username}.`);
          } else if (result.isFailed()) {
            fail("signIn", result.error, step.username);
          }
          return;
        }

        case "signUpCode": {
          const result = await step.state.resendCode();
          if (result.isCodeRequired()) {
            setStep({ ...step, state: result.state, codeLength: result.state.getCodeLength() });
            startResendCooldown(result.state.getCodeResendInterval());
            setInfo(`We sent a new code to ${step.username}.`);
          } else if (result.isFailed()) {
            fail("signUp", result.error, step.username);
          }
          return;
        }

        case "resetCode": {
          const result = await step.state.resendCode();
          if (result.isCodeRequired()) {
            setStep({ ...step, state: result.state, codeLength: result.state.getCodeLength() });
            startResendCooldown(DEFAULT_RESEND_SECONDS);
            setInfo(`We sent a new code to ${step.username}.`);
          } else if (result.isFailed()) {
            fail("resetPassword", result.error, step.username);
          }
          return;
        }
      }
    });

  const goTo = (view: NativeAuthView) => {
    setStep(initialStep(view));
    setError(null);
    setInfo(null);
  };

  return {
    step,
    view: viewOf(step),
    busy,
    error,
    info,
    resendAvailableAt,
    startSignIn,
    startSignUp,
    startResetPassword,
    submitCode,
    submitPassword,
    resendCode,
    goTo,
  };
}
